import { Artwork } from './artworkApi';
import type { BrowseSortBy } from './browseApi';
import { Collection } from '../types/collection';
import { parseCreationDateToTimestamp } from './creationDateParser';
import { logger } from './logger';

/**
 * Available fields for sorting artwork
 */
export type SortField = 'title' | 'artworkId' | 'createdAt' | 'creationDate' | 'customNumber';

/**
 * Sort direction
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Complete sort option including field, direction, and display label
 */
export interface SortOption {
  field: SortField;
  direction: SortDirection;
  label: string;
}

/**
 * localStorage key for persisting sort preference.
 * Versioned (v2) to reset preferences after adding creationDate sort options,
 * which changed the meaning of "Newest First" from createdAt to creationDate.
 */
export const SORT_STORAGE_KEY = 'legacy-catalog-sort-preference-v2';

/**
 * All available sort options with labels
 */
export const SORT_OPTIONS: SortOption[] = [
  { field: 'creationDate', direction: 'desc', label: 'Newest First' },
  { field: 'creationDate', direction: 'asc', label: 'Oldest First' },
  { field: 'title', direction: 'asc', label: 'Title (A-Z)' },
  { field: 'title', direction: 'desc', label: 'Title (Z-A)' },
  { field: 'artworkId', direction: 'asc', label: 'Catalog # Ascending' },
  { field: 'artworkId', direction: 'desc', label: 'Catalog # Descending' },
];

/**
 * Default sort: newest first (most intuitive for catalog management)
 */
export const DEFAULT_SORT: SortOption = SORT_OPTIONS[0];

/**
 * Parse a catalog ID (LL-NXX.NNNN) into prefix and numeric sequence.
 */
export const parseCatalogId = (id: string): { prefix: string; sequence: number } => {
  const dotIndex = id.indexOf('.');
  if (dotIndex === -1) {
    return { prefix: id.toLowerCase(), sequence: 0 };
  }
  return {
    prefix: id.substring(0, dotIndex).toLowerCase(),
    sequence: parseInt(id.substring(dotIndex + 1), 10) || 0,
  };
};

/**
 * Compute a stable tiebreaker comparison between two artworks.
 *
 * Used as a secondary sort key when the primary sort field compares equal.
 * Prefers aliCatalogNumber (catalog ID order) and falls back to artworkId UUID
 * so that the sort is always deterministic regardless of API response order.
 * This prevents the "scrambled" appearance in the public catalog view (#932).
 *
 * @param a - First artwork
 * @param b - Second artwork
 * @returns Negative if a < b, positive if a > b, 0 if equal
 */
const stableTiebreaker = (a: Artwork, b: Artwork): number => {
  const catA = a.aliCatalogNumber;
  const catB = b.aliCatalogNumber;

  if (catA && catB) {
    const idA = parseCatalogId(catA);
    const idB = parseCatalogId(catB);
    const prefixCmp = idA.prefix.localeCompare(idB.prefix);
    if (prefixCmp !== 0) return prefixCmp;
    if (idA.sequence !== idB.sequence) return idA.sequence - idB.sequence;
  } else if (!catA && catB) {
    return 1;
  } else if (catA && !catB) {
    return -1;
  }

  // Final fallback: UUID artworkId (always unique, always present)
  return a.artworkId.localeCompare(b.artworkId);
};

/**
 * Sort an array of artworks by the specified field and direction.
 * Returns a new array - does not mutate the original.
 *
 * When items compare equal on the primary sort field (e.g. both lack a
 * creationDate), a stable tiebreaker using aliCatalogNumber → artworkId is
 * applied so the result is deterministic regardless of the order the backend
 * returns items. This is important for the public catalog view where items
 * arrive in arbitrary DynamoDB scan order (#932).
 *
 * @param artworks - Array of artworks to sort
 * @param option - Sort option containing field and direction
 * @returns New sorted array
 */
export const sortArtworks = (artworks: Artwork[], option: SortOption): Artwork[] => {
  // Create a copy to avoid mutating the original array
  const sorted = [...artworks];

  sorted.sort((a, b) => {
    let comparison = 0;

    switch (option.field) {
      case 'title': {
        // Get titles, treating null/undefined/empty as empty string for sorting
        const titleA = (a.title || '').toLowerCase();
        const titleB = (b.title || '').toLowerCase();

        // Empty titles should sort to the end
        if (!titleA && titleB) return 1;
        if (titleA && !titleB) return -1;
        if (!titleA && !titleB) return stableTiebreaker(a, b);

        comparison = titleA.localeCompare(titleB);
        break;
      }

      case 'artworkId': {
        // Sort by aliCatalogNumber (LL-NXX.NNNN), not by the UUID artworkId field.
        // Items without an aliCatalogNumber sort to the end regardless of direction.
        const catA = a.aliCatalogNumber;
        const catB = b.aliCatalogNumber;
        if (!catA && catB) return 1;
        if (catA && !catB) return -1;
        if (!catA && !catB) return stableTiebreaker(a, b);
        const idA = parseCatalogId(catA!);
        const idB = parseCatalogId(catB!);
        const prefixCmp = idA.prefix.localeCompare(idB.prefix);
        comparison = prefixCmp !== 0 ? prefixCmp : idA.sequence - idB.sequence;
        break;
      }

      case 'creationDate': {
        const tsA = parseCreationDateToTimestamp(a.creationDate);
        const tsB = parseCreationDateToTimestamp(b.creationDate);
        const hasA = !isNaN(tsA);
        const hasB = !isNaN(tsB);
        // Items without dates sort to end regardless of direction
        if (!hasA && hasB) return 1;
        if (hasA && !hasB) return -1;
        if (!hasA && !hasB) return stableTiebreaker(a, b);
        comparison = tsA - tsB;
        break;
      }

      case 'createdAt': {
        // Compare dates
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        comparison = dateA - dateB;
        break;
      }

      default:
        // Fallback - no sorting
        comparison = 0;
    }

    // Apply direction; use tiebreaker when comparison is equal
    if (comparison !== 0) {
      return option.direction === 'desc' ? -comparison : comparison;
    }
    return stableTiebreaker(a, b);
  });

  return sorted;
};

/**
 * Validate that a sort option is valid.
 * Exported so page-scoped loaders (e.g. loadMyObjectsSortPreference) and tests can reuse it.
 */
export const isValidSortOption = (option: unknown): option is SortOption => {
  if (!option || typeof option !== 'object') return false;

  const opt = option as Record<string, unknown>;
  const validFields: SortField[] = ['title', 'artworkId', 'createdAt', 'creationDate', 'customNumber'];
  const validDirections: SortDirection[] = ['asc', 'desc'];

  return (
    validFields.includes(opt.field as SortField) &&
    validDirections.includes(opt.direction as SortDirection) &&
    typeof opt.label === 'string'
  );
};

/**
 * Load sort preference from localStorage.
 * Returns DEFAULT_SORT if no preference is stored or if stored value is invalid.
 */
export const loadSortPreference = (): SortOption => {
  try {
    const stored = localStorage.getItem(SORT_STORAGE_KEY);
    if (!stored) return DEFAULT_SORT;

    const parsed = JSON.parse(stored);
    if (!isValidSortOption(parsed)) return DEFAULT_SORT;

    // Find matching option from SORT_OPTIONS to ensure we have correct label
    const match = SORT_OPTIONS.find(
      (opt) => opt.field === parsed.field && opt.direction === parsed.direction
    );

    return match || DEFAULT_SORT;
  } catch (error) {
    // Invalid JSON or other error
    logger.debug('Failed to load sort preference from localStorage', error);
    return DEFAULT_SORT;
  }
};

/**
 * Save sort preference to localStorage.
 * Silently fails if localStorage is unavailable or full.
 */
export const saveSortPreference = (option: SortOption): void => {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(option));
  } catch (error) {
    logger.debug('Failed to save sort preference to localStorage', error);
  }
};

// ============================================================================
// My Objects page — scoped sort options
// ============================================================================

/**
 * Extra sort options exposed only on the My Objects page. Sorts by the FIRST
 * inventory number the user entered on each item (index 0 of customCatalogNumbers).
 */
export const MY_OBJECTS_SORT_OPTIONS: SortOption[] = [
  ...SORT_OPTIONS,
  { field: 'customNumber', direction: 'asc', label: 'Inventory # Ascending' },
  { field: 'customNumber', direction: 'desc', label: 'Inventory # Descending' },
];

/**
 * Page-scoped storage key — keeps the inventory-number preference isolated to
 * My Objects so other pages (e.g. public Browse) don't silently fall back to
 * default when the preference field isn't in their own sort option list.
 */
export const MY_OBJECTS_SORT_STORAGE_KEY = 'legacy-catalog-my-objects-sort-v1';

export const loadMyObjectsSortPreference = (): SortOption => {
  try {
    const stored = localStorage.getItem(MY_OBJECTS_SORT_STORAGE_KEY);
    if (!stored) return DEFAULT_SORT;

    const parsed = JSON.parse(stored);
    if (!isValidSortOption(parsed)) return DEFAULT_SORT;

    const match = MY_OBJECTS_SORT_OPTIONS.find(
      (opt) => opt.field === parsed.field && opt.direction === parsed.direction
    );
    return match || DEFAULT_SORT;
  } catch (error) {
    logger.debug('Failed to load My Objects sort preference from localStorage', error);
    return DEFAULT_SORT;
  }
};

export const saveMyObjectsSortPreference = (option: SortOption): void => {
  try {
    localStorage.setItem(MY_OBJECTS_SORT_STORAGE_KEY, JSON.stringify(option));
  } catch (error) {
    logger.debug('Failed to save My Objects sort preference to localStorage', error);
  }
};

/**
 * Map a client-side SortOption to a server-side BrowseSortBy value.
 * Used when switching from client-side sorting to server-side (OpenSearch) sorting.
 */
const SORT_BY_MAP: Record<string, Record<string, BrowseSortBy>> = {
  creationDate: { desc: 'year_desc', asc: 'year_asc' },
  title: { asc: 'title_asc', desc: 'title_desc' },
  artworkId: { asc: 'catalog_asc', desc: 'catalog_desc' },
  createdAt: { desc: 'newest', asc: 'oldest' },
  customNumber: { asc: 'custom_asc', desc: 'custom_desc' },
};

export const toSearchSortBy = (option: SortOption): BrowseSortBy => {
  return SORT_BY_MAP[option.field]?.[option.direction] ?? 'newest';
};

// ============================================================================
// Sort field options (for SortFieldSelector component)
// ============================================================================

/**
 * A sort field option without direction — used by the SortFieldSelector
 * where field and direction are selected independently.
 */
export interface SortFieldOption {
  field: SortField;
  label: string;
}

/**
 * Available sort fields for the compact field selector.
 * Direction is controlled separately via a toggle button.
 */
export const SORT_FIELD_OPTIONS: SortFieldOption[] = [
  { field: 'creationDate', label: 'Date' },
  { field: 'title', label: 'Title' },
  { field: 'artworkId', label: 'Catalog #' },
];

/**
 * Get a direction arrow character for display.
 */
export const getDirectionArrow = (direction: SortDirection): string =>
  direction === 'desc' ? '↓' : '↑';

/**
 * Get the default sort direction for a given field.
 */
export const getDefaultDirection = (field: SortField): SortDirection => {
  switch (field) {
    case 'title':
      return 'asc';
    default:
      return 'desc';
  }
};

// ============================================================================
// Collection-specific sorting
// ============================================================================

/**
 * Sort options for collections (name instead of title, no artworkId)
 */
export const COLLECTION_SORT_OPTIONS: SortOption[] = [
  { field: 'createdAt', direction: 'desc', label: 'Newest First' },
  { field: 'createdAt', direction: 'asc', label: 'Oldest First' },
  { field: 'title', direction: 'asc', label: 'Name (A-Z)' },
  { field: 'title', direction: 'desc', label: 'Name (Z-A)' },
];

/**
 * Default sort for collections: newest first
 */
export const DEFAULT_COLLECTION_SORT: SortOption = COLLECTION_SORT_OPTIONS[0];

/**
 * Sort an array of collections by the specified field and direction.
 * Maps 'title' field to 'name' property on collections.
 * Returns a new array - does not mutate the original.
 *
 * @param collections - Array of collections to sort
 * @param option - Sort option containing field and direction
 * @returns New sorted array
 */
export const sortCollections = (collections: Collection[], option: SortOption): Collection[] => {
  // Create a copy to avoid mutating the original array
  const sorted = [...collections];

  sorted.sort((a, b) => {
    let comparison = 0;

    switch (option.field) {
      case 'title': {
        // For collections, 'title' maps to 'name'
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();

        // Empty names should sort to the end
        if (!nameA && nameB) return 1;
        if (nameA && !nameB) return -1;
        if (!nameA && !nameB) return 0;

        comparison = nameA.localeCompare(nameB);
        break;
      }

      case 'createdAt': {
        // Compare dates
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        comparison = dateA - dateB;
        break;
      }

      default:
        // Fallback - no sorting
        comparison = 0;
    }

    // Apply direction
    return option.direction === 'desc' ? -comparison : comparison;
  });

  return sorted;
};
